// src/helpers/searchPropiedades.js
import { collection, query, where, getDocs, limit } from "firebase/firestore";

export function norm(s = "") {
  return s
    .toString()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .toUpperCase()
    .replace(/\s+/g, " ")
    .trim();
}

function dedup(arr) {
  const map = new Map();
  for (const x of arr) map.set(x.id, x);
  return Array.from(map.values());
}

function gramsFromTerm(term) {
  const t = norm(term);
  const set = new Set();
  if (t.length >= 3) for (let i = 0; i <= t.length - 3; i++) set.add(t.slice(i, i + 3)); // tri
  if (t.length >= 2) for (let i = 0; i <= t.length - 2; i++) set.add(t.slice(i, i + 2)); // bi
  // Firestore limita array-contains-any a 10 elementos
  return Array.from(set).slice(0, 10);
}

/**
 * Búsqueda flexible:
 * - Folio permisivo: "PR-00012", "pr12" → folio exacto
 * - MZ/LT: "MZ7 LT12", "7-12", "7 12" → claveLote o manzana/lote
 * - Número solo: busca manzana o lote (prioriza fracc si hay)
 * - Contiene (subcadenas): n-grams en buscadorNgram + filtro final en cliente
 * Devuelve { items, intent }
 */
export async function searchPropiedadesFlexible(db, rawTerm, fraccId = "") {
  const tRaw = (rawTerm ?? "").trim();
  const t = norm(tRaw);
  if (!t) return { items: [], intent: "VACÍO" };

  const col = collection(db, "propiedades");
  let results = [];
  let intent = "TEXTO";

  // --- 1) FOLIO (permisivo: PR00012, PR-12, pr 12) ---
  const folioMatch = t.replace(/[\s]/g, "");
  if (/^[A-Z]{2,}-?\d+$/i.test(folioMatch)) {
    intent = "FOLIO";
    const up = folioMatch.toUpperCase();
    const m = up.match(/^([A-Z]{2,})-?(\d+)$/);
    if (m) {
      const prefix = m[1];
      const num = m[2].padStart(5, "0");
      const folioNorm = `${prefix}-${num}`;
      const q1 = fraccId
        ? query(col, where("folio", "==", folioNorm), where("fraccionamientoId", "==", fraccId), limit(50))
        : query(col, where("folio", "==", folioNorm), limit(50));
      const s1 = await getDocs(q1);
      results = dedup(s1.docs.map(d => ({ id: d.id, ...d.data() })));
      if (results.length) return { items: results, intent };
    }
  }

  // --- 2) MZ/LT: "MZ7 LT12" | "7-12" | "7 12" ---
  const mzlt = t.replace(/\s+/g, " ");
  let mz = null, lt = null;
  const m1 = mzlt.match(/MZ\s*(\d+)\D+LT\s*(\d+)/);
  const m2 = mzlt.match(/^(\d+)\D+(\d+)$/);
  if (m1) { mz = Number(m1[1]); lt = Number(m1[2]); }
  else if (m2) { mz = Number(m2[1]); lt = Number(m2[2]); }

  if (mz != null && lt != null) {
    intent = "MZ/LT";
    if (fraccId) {
      const clave = `${fraccId}|${mz}|${lt}`;
      const q2 = query(col, where("claveLote", "==", clave), limit(50));
      const s2 = await getDocs(q2);
      results = dedup(s2.docs.map(d => ({ id: d.id, ...d.data() })));
      if (results.length) return { items: results, intent };
    } else {
      // requiere índice compuesto manzanaInt + loteInt
      const q3 = query(col, where("manzanaInt", "==", mz), where("loteInt", "==", lt), limit(50));
      const s3 = await getDocs(q3);
      results = dedup(s3.docs.map(d => ({ id: d.id, ...d.data() })));
      if (results.length) return { items: results, intent };
    }
  }

  // --- 3) Número solo: manzana o lote (prioriza fracc si existe) ---
  if (/^\d+$/.test(t)) {
    intent = "NÚMERO (MZ/LT)";
    const n = Number(t);
    if (fraccId) {
      const q4 = query(col, where("fraccionamientoId", "==", fraccId), where("manzanaInt", "==", n), limit(50));
      const s4 = await getDocs(q4);
      const q5 = query(col, where("fraccionamientoId", "==", fraccId), where("loteInt", "==", n), limit(50));
      const s5 = await getDocs(q5);
      results = dedup([
        ...s4.docs.map(d => ({ id: d.id, ...d.data() })),
        ...s5.docs.map(d => ({ id: d.id, ...d.data() })),
      ]);
      if (results.length) return { items: results.slice(0, 50), intent };
    } else {
      const q6 = query(col, where("manzanaInt", "==", n), limit(25));
      const s6 = await getDocs(q6);
      const q7 = query(col, where("loteInt", "==", n), limit(25));
      const s7 = await getDocs(q7);
      results = dedup([
        ...s6.docs.map(d => ({ id: d.id, ...d.data() })),
        ...s7.docs.map(d => ({ id: d.id, ...d.data() })),
      ]);
      if (results.length) return { items: results.slice(0, 50), intent };
    }
  }

  // --- 4) CONTIENE por n-gramas (bigrama/trigrama) + filtro en cliente ---
  // requiere campo "buscadorNgram" en cada propiedad (ver buildPropDoc)
  const grams = gramsFromTerm(tRaw);
  if (grams.length) {
    intent = "CONTIENTE";
    const q = fraccId
      ? query(col, where("fraccionamientoId", "==", fraccId), where("buscadorNgram", "array-contains-any", grams), limit(50))
      : query(col, where("buscadorNgram", "array-contains-any", grams), limit(50));
    const snap = await getDocs(q);
    let cand = snap.docs.map(d => ({ id: d.id, ...d.data() }));

    // filtro final en cliente: valida que el texto normalizado realmente CONTIENE el término completo
    const needle = t;
    cand = cand.filter(r => {
      const haystack = norm(
        `${r.folio || ""} ${r.nombre || ""} ${r.fraccNombre || ""} ${r.ubicacion || ""} ${r.tipo || ""} ${r.codigoHumano || ""}`
      );
      return haystack.includes(needle);
    });

    return { items: dedup(cand).slice(0, 50), intent };
  }

  // --- 5) Fallback: tokens exactos ---
  intent = "TEXTO";
  const q8 = fraccId
    ? query(col, where("fraccionamientoId", "==", fraccId), where("buscadorTokens", "array-contains", t), limit(50))
    : query(col, where("buscadorTokens", "array-contains", t), limit(50));
  const s8 = await getDocs(q8);
  results = dedup(s8.docs.map(d => ({ id: d.id, ...d.data() })));

  return { items: results.slice(0, 50), intent };
}
